import React, { FC, ReactNode } from 'react';
import { SelectOption } from 'global';
import { grey } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Button, Drawer, Stack, Typography } from '@mui/material';

import { BaseActionsProps, BaseUIProps } from './types';
import { FilterCardContainer } from './FilterCardContainer';

interface Props<FilterType extends string> extends BaseUIProps {
  presetsNode?: ReactNode;
  headerNode?: ReactNode;
  actions: BaseActionsProps;
  isApplyDisabled?: boolean;
  configuration: FilterType[];
  externalMeta?: Partial<Record<FilterType, SelectOption[]>>;
  componentMap: Record<
    FilterType,
    { label: string; component: FC<{ meta?: Partial<Record<FilterType, SelectOption[]>> }> }
  >;
}

export const FiltersDrover = <FilterType extends string>({
  open,
  onClose,
  actions,
  headerNode,
  presetsNode,
  componentMap,
  externalMeta = {},
  configuration = [],
  isApplyDisabled = false,
}: Props<FilterType>) => {
  const classes = useStyles();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack className={classes.container}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" className={classes.body}>
          <Typography variant="h6" component="span">
            Filters
          </Typography>

          {headerNode && headerNode}
        </Stack>

        {presetsNode && presetsNode}

        <Stack gap="0.5rem" height="100%" overflow="auto" paddingBottom="0.5rem">
          {configuration.map(type => {
            const Component = componentMap[type].component;

            return (
              <FilterCardContainer key={componentMap[type].label} label={componentMap[type].label}>
                <Component meta={externalMeta} />
              </FilterCardContainer>
            );
          })}
        </Stack>

        <Box className={classes.actions}>
          <Button disabled={isApplyDisabled} onClick={actions.onReset}>
            Reset All
          </Button>

          <Button variant="contained" onClick={actions.onApply}>
            Apply
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

const useStyles = makeStyles<Theme>(({ palette, shadows }) => ({
  container: {
    gap: '0.5rem',
    height: '100%',
    maxWidth: '30rem',
    minWidth: '30rem',
    overflow: 'hidden',
    backgroundColor: grey[100],
  },
  body: {
    padding: '1rem',
    boxShadow: shadows[1],
    borderRadius: '0.5rem',
    margin: '0.5rem 0.5rem 0 0.5rem',
    backgroundColor: palette.background.paper,
  },
  actions: {
    gap: '0.5rem',
    display: 'flex',
    boxShadow: shadows[1],
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    justifyContent: 'flex-end',
    margin: '0 0.5rem 0.5rem 0.5rem',
    backgroundColor: palette.background.paper,
  },
}));
